<template>
  <div class="relative w-full max-w-7xl mx-auto my-16 overflow-hidden">
    <!-- Decorative background elements -->
    <div class="absolute -top-32 -left-32 w-80 h-80 bg-gradient-to-tr from-primary/30 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
    <div class="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-secondary/20 to-transparent rounded-full blur-2xl animate-float-delayed"></div>
    
    
    <!-- Header with dynamic styling -->
    <div class="relative flex justify-start mb-32">
      
      <h2 
        class="relative text-4xl md:text-6xl font-bold text-white z-10 flex flex-row ml-4 md:ml-0 md:flex-row items-center  gap-3"
        ref="headerContainer"
      >
        We want 
        <span 
          class="text-secondary font-bold text-5xl md:text-7xl text-shadow-lg transform transition-all duration-500 hover:scale-105 cursor-pointer"
          ref="youText"
          @mouseenter="animateYouText"
          @mouseleave="resetYouText"
        >
          YOU!
        </span>
      </h2>
    </div>
    
    <!-- Main content section -->
    <div class="relative px-4 w-full">
      <div class="relative flex flex-col md:flex-row justify-end h-130 items-end gap-8">
        <!-- Image with parallax effect -->
        <div
          class="absolute left-0 -top-30 w-5/5 h-130 overflow-hidden rounded-2xl shadow-2xl"
          ref="imageContainer"
          @mousemove="handleImageParallax"
          @mouseleave="resetImageParallax"
        >
          <!-- Image overlays and effects -->
          
          <!-- Main recruitment image -->
          <img
            src="/img/WeWantYou.jpg"
            alt="Join Us!"
            class="absolute top-10 inset-0 w-full h-full object-cover transition-transform duration-500 ease-out scale-105 z-0 brightness-[0.8]"
            ref="parallaxImage"
          />
          
          
        </div>
        
        <!-- Content card with glass morphism -->
        <div
          class="relative w-full md:w-3/5 p-6 md:p-8 rounded-2xl backdrop-blur-md 
                bg-gradient-to-br from-primary/90 to-primary/70 
                border border-white/10 shadow-xl overflow-hidden group"
          :class="{'shadow-lg shadow-primary/30': isHovered}"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <!-- Card background effects -->
          <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.1),_transparent_70%)] z-0"></div>
          <div 
            class="absolute inset-0 bg-gradient-to-tr from-secondary/5 via-transparent to-secondary/10 
                  opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-0"
          ></div>
          
          <!-- Content with entrance animations -->
          <div class="relative z-10">
            <p 
              class="text-lg md:text-xl text-white leading-relaxed mb-6 transform transition-all duration-700 text-left"
              :class="{'translate-y-0 opacity-100': isVisible, '-translate-y-6 opacity-0': !isVisible}"
            >
              Are you motivated? Do you want to work in a growing business in a brand new, high-tech automated factory? Then you've come to the right place! Click to learn more about available opportunities here at STE.
            </p>
            
            <!-- Button container with flexbox to position buttons on opposite sides -->
            <div 
              class="flex justify-between items-center transform transition-all duration-500 delay-300"
              :class="{'translate-y-0 opacity-100': isVisible, 'translate-y-8 opacity-0': !isVisible}"
            >
              <!-- Meet the Team button (left side) -->
              <button 
                class="relative overflow-hidden py-3 px-2 md:px-8 rounded-full bg-white text-primary font-bold text-lg cursor-pointer
                       transition-all duration-300 hover:shadow-lg hover:shadow-white/30 hover:scale-105
                       focus:outline-none focus:ring-2 focus:ring-white/20 active:scale-95 group"
                @click="navigateToTeamSection"
              >
                <!-- Arrow icon pointing left that animates on hover -->
                <font-awesome-icon 
                  :icon="['fas', 'left-long']" 
                  class="mr-2 relative z-10 transition-transform duration-300 group-hover:-translate-x-1" 
                />
                
                <!-- Button content -->
                <span class="relative z-10">Meet the Team</span>
                
                <!-- Button hover effect (reversed direction) -->
                <div 
                  class="absolute inset-0 origin-right scale-x-0 group-hover:scale-x-100 
                         bg-secondary/20 transition-transform duration-500"
                ></div>
              </button>
              
              <!-- Apply now button (right side) -->
              <button 
                class="relative overflow-hidden py-3 px-2 md:px-8 rounded-full bg-white text-primary font-bold text-lg
                       transition-all duration-300 hover:shadow-lg hover:shadow-white/30 hover:scale-105 cursor-pointer
                       focus:outline-none focus:ring-2 focus:ring-white/20 active:scale-95 group"
                @click="navigateToApplicationPage"
              >
                <!-- Button content -->
                <span class="relative z-10">Apply now</span>
                
                <!-- Button hover effect -->
                <div 
                  class="absolute inset-0 origin-left scale-x-0 group-hover:scale-x-100 
                         bg-secondary/20 transition-transform duration-500"
                ></div>
                
                <!-- Arrow icon that animates on hover -->
                <font-awesome-icon 
                  :icon="['fas', 'right-long']" 
                  class="ml-2 relative z-10 transition-transform duration-300 group-hover:translate-x-1" 
                />
              </button>
            </div>
          </div>
          
          <!-- Decorative corner elements -->
          <div class="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2 border-white/20 rounded-tr-2xl"></div>
          <div class="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-white/10 rounded-bl-xl"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JoinUsBox',
  data() {
    return {
      isHovered: false,
      isVisible: false,
    };
  },
  mounted() {
    // Initialize animations after component is mounted
    setTimeout(() => {
      this.isVisible = true;
    }, 300);
    
    // Setup intersection observer for scroll animations
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    // Clean up observer
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    navigateToApplicationPage() {
      // Add a slight delay for animation effect
      setTimeout(() => {
        this.$router.push('/careers');
      }, 200);
    },
    
    /**
     * Navigate to the Meet The Team section on AboutUsPage
     */
    navigateToTeamSection() {
      // Add a slight delay for animation effect
      setTimeout(() => {
        this.$router.push({
          path: '/about-us',
          hash: '#slide3'  // ID of the Meet The Team section
        });
      }, 200);
    },
    
    // 3D text effect for the "YOU!" text
    animateYouText() {
      if (!this.$refs.youText) return;
      
      const el = this.$refs.youText;
      el.style.textShadow = '4px 4px 0px #000, 0 0 30px rgba(183, 185, 186, 0.3)';
      el.style.transform = 'scale(1.1) rotate(-3deg)';
    },
    
    resetYouText() {
      if (!this.$refs.youText) return;
      
      const el = this.$refs.youText;
      el.style.textShadow = '3px 3px 0px #000';
      el.style.transform = '';
    },
    
    // Parallax effect for the image
    handleImageParallax(e) {
      const container = this.$refs.imageContainer;
      if (!container || !this.$refs.parallaxImage) return;
      
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Calculate the center of the container
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      // Calculate the percentage from the center
      const moveX = ((x - centerX) / centerX) * -15; // Adjust intensity
      const moveY = ((y - centerY) / centerY) * -10;
      
      // Apply parallax effect
      this.$refs.parallaxImage.style.transform = `translate(${moveX}px, ${moveY}px) scale(1.15)`;
    },
    
    resetImageParallax() {
      if (this.$refs.parallaxImage) {
        this.$refs.parallaxImage.style.transform = 'scale(1.05)';
      }
    },
    
    // Scroll animation detection
    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.isVisible = true;
            } else {
              // Optional: Reset animation when out of view
              // this.isVisible = false; 
            }
          });
        }, { threshold: 0.2 });
        
        this.observer.observe(this.$el);
      } else {
        // Fallback for browsers that don't support IntersectionObserver
        this.isVisible = true;
      }
    }
  }
};
</script>

<style scoped>
/* Animation keyframes */
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-12px) rotate(2deg); }
}

@keyframes float-slow {
  0%, 100% { transform: translateY(0) rotate(0); }
  50% { transform: translateY(-18px) rotate(-3deg); }
}

@keyframes float-delayed {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
}

@keyframes spin-slow {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

/* Animation classes */
.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 12s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 15s ease-in-out infinite 2s;
}

.animate-pulse-slow {
  animation: pulse-slow 10s ease-in-out infinite;
}

.animate-spin-slow {
  animation: spin-slow 30s linear infinite;
}

/* Text shadow */
.text-shadow-lg {
  text-shadow: 3px 3px 0px #000;
}

/* Tech-themed background patterns */
.circuit-pattern {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 304 304' width='304' height='304'%3E%3Cpath fill='%23ffffff' fill-opacity='1' d='M44.1 224a5 5 0 1 1 0 2H0v-2h44.1zm160 48a5 5 0 1 1 0 2H82v-2h122.1zm57.8-46a5 5 0 1 1 0-2H304v2h-42.1zm0 16a5 5 0 1 1 0-2H304v2h-42.1zm6.2-114a5 5 0 1 1 0 2h-86.2a5 5 0 1 1 0-2h86.2zm-256-48a5 5 0 1 1 0 2H0v-2h12.1zm185.8 34a5 5 0 1 1 0-2h86.2a5 5 0 1 1 0 2h-86.2zM258 12.1a5 5 0 1 1-2 0V0h2v12.1zm-64 208a5 5 0 1 1-2 0v-54.2a5 5 0 1 1 2 0v54.2zm48-198.2V80h62v2h-64V21.9a5 5 0 1 1 2 0zm16 16V64h46v2h-48V37.9a5 5 0 1 1 2 0zm-128 96V208h16v12.1a5 5 0 1 1-2 0V210h-16v-76.1a5 5 0 1 1 2 0zm-5.9-21.9a5 5 0 1 1 0 2H114v48H85.9a5 5 0 1 1 0-2H112v-48h12.1zm-6.2 130a5 5 0 1 1 0-2H176v-74.1a5 5 0 1 1 2 0V242h-60.1zm-16-64a5 5 0 1 1 0-2H114v48h10.1a5 5 0 1 1 0 2H112v-48h-10.1zM66 284.1a5 5 0 1 1-2 0V274H50v30h-2v-32h18v12.1zM236.1 176a5 5 0 1 1 0 2H226v94h48v32h-2v-30h-48v-98h12.1zm25.8-30a5 5 0 1 1 0-2H274v44.1a5 5 0 1 1-2 0V146h-10.1zm-64 96a5 5 0 1 1 0-2H208v-80h16v-14h-42.1a5 5 0 1 1 0-2H226v18h-16v80h-12.1zm86.2-210a5 5 0 1 1 0 2H272V0h2v32h10.1zM98 101.9V146H53.9a5 5 0 1 1 0-2H96v-42.1a5 5 0 1 1 2 0zM53.9 34a5 5 0 1 1 0-2H80V0h2v34H53.9zm60.1 3.9V66H82v64H69.9a5 5 0 1 1 0-2H80V64h32V37.9a5 5 0 1 1 2 0zM101.9 82a5 5 0 1 1 0-2H128V37.9a5 5 0 1 1 2 0V82h-28.1zm16-64a5 5 0 1 1 0-2H146v44.1a5 5 0 1 1-2 0V18h-26.1zm102.2 270a5 5 0 1 1 0 2H98v14h-2v-16h124.1zM242 149.9V160h16v34h-16v62h48v48h-2v-46h-48v-66h16v-30h-16v-12.1a5 5 0 1 1 2 0zM53.9 18a5 5 0 1 1 0-2H64V2H48V0h18v18H53.9zm112 32a5 5 0 1 1 0-2H192V0h50v2h-48v48h-28.1zm-48-48a5 5 0 0 1-9.8-2h2.07a3 3 0 1 0 5.66 0H178v34h-18V21.9a5 5 0 1 1 2 0V32h14V2h-58.1zm0 96a5 5 0 1 1 0-2H137l32-32h39V21.9a5 5 0 1 1 2 0V66h-40.17l-32 32H117.9zm28.1 90.1a5 5 0 1 1-2 0v-76.51L175.59 80H224V21.9a5 5 0 1 1 2 0V82h-49.59L146 112.41v75.69zm16 32a5 5 0 1 1-2 0v-99.51L184.59 96H300.1a5 5 0 0 1 3.9-3.9v2.07a3 3 0 0 0 0 5.66v2.07a5 5 0 0 1-3.9-3.9H185.41L162 121.41v98.69zm-144-64a5 5 0 1 1-2 0v-3.51l48-48V48h32V0h2v50H66v55.41l-48 48v2.69zM50 53.9v43.51l-48 48V208h26.1a5 5 0 1 1 0 2H0v-65.41l48-48V53.9a5 5 0 1 1 2 0zm-16 16V89.41l-34 34v-2.82l32-32V69.9a5 5 0 1 1 2 0z'%3E%3C/path%3E%3C/svg%3E");
}

.hexagon-pattern {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/svg%3E");
}

/* Media queries for responsive design */
@media (max-width: 768px) {
  .animate-float, .animate-float-slow, .animate-float-delayed {
    animation-duration: 15s;
  }
}

/* Improve touch device interactions */
@media (hover: none) {
  .group:active .group-hover\:opacity-100 {
    opacity: 1;
  }
  
  .group:active .group-hover\:scale-x-100 {
    transform: scaleX(1);
  }
  
  .group:active .group-hover\:translate-x-1 {
    transform: translateX(0.25rem);
  }
}
</style>
